import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Mail, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { mitgliedRechnungenAPI } from '@/lib/api';
import { useUmsatz } from '@/components/UmsatzSperre';

/* Reiter „Rechnungen" am Mitglied (15.10.2026, Betreiber: „wie in Lexware je Kunde").
   Beide Quellen in einer Liste: Rechnungslauf des CRM (Positionen, Einzug, Rücklastschrift) und der
   Lexware-Spiegel (Altbestand, Serienrechnungen, Shop). PDF kommt frisch aus Lexware, „Per Mail senden"
   schickt es an den Zahler. Nur mit Recht finanzen.view — die Seite blendet den Reiter sonst aus. */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }));
const datum = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00`).toLocaleDateString('de-DE') : '—');
const STATUS = {
  offen: ['offen', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  im_einzug: ['Einzug geplant', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  eingezogen: ['eingezogen', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  bezahlt: ['bezahlt', 'bg-emerald-600 text-white'],
  ruecklastschrift: ['Rücklastschrift', 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'],
  zurueckgestellt: ['zurückgestellt', 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'],
  storniert: ['storniert', 'bg-muted text-muted-foreground line-through'],
};
const VERSAND = { gesendet: 'gesendet', keine_email: 'keine E-Mail', geblockt: 'geblockt', fehler: 'Fehler', abruf_fehler: 'PDF-Abruf fehlgeschlagen', deaktiviert: 'Versand aus', in_arbeit: 'läuft' };

function Chip({ k }) {
  const [lab, cls] = STATUS[k] || [k, 'bg-muted'];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${cls}`}>{lab}</span>;
}

export default function MitgliedRechnungen({ leadId }) {
  const [d, setD] = useState(null);
  const { inhaber, anfordern } = useUmsatz();
  const [laden, setLaden] = useState(true);
  const [busy, setBusy] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    try { setD((await mitgliedRechnungenAPI.liste(leadId)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Rechnungen konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, [leadId]);
  useEffect(() => { lade(); }, [lade]);

  const pdf = async (r) => {
    setBusy(`pdf-${r.id}`);
    try {
      const res = await mitgliedRechnungenAPI.pdf(leadId, r.id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let text = 'PDF nicht abrufbar';
      try { text = JSON.parse(await e?.response?.data?.text())?.detail || text; } catch { /* Blob ohne JSON */ }
      toast.error(text);
    } finally { setBusy(''); }
  };

  const senden = async (r) => {
    if (!window.confirm(`Rechnung ${r.nummer || ''} per E-Mail an den Zahler schicken?`)) return;
    setBusy(`mail-${r.id}`);
    try {
      const v = (await mitgliedRechnungenAPI.senden(leadId, r.id)).data;
      if (v.status === 'gesendet') toast.success(`Gesendet an ${v.an}`);
      else toast.warning(`Nicht gesendet: ${VERSAND[v.status] || v.status}${v.grund ? ` — ${v.grund}` : ''}`);
      lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  if (laden && !d) return <p className="text-sm text-muted-foreground py-6 text-center">Rechnungen werden geladen …</p>;
  const liste = d?.rechnungen || [];
  const summen = d?.summen || {};
  const jahre = Object.entries(summen.je_jahr || {});

  return (
    <div className="space-y-4" data-testid="mitglied-rechnungen">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Card><CardContent className="p-3"><div className="text-xs text-muted-foreground">Rechnungen</div><div className="text-xl font-extrabold tabular-nums">{summen.anzahl ?? 0}</div></CardContent></Card>
          <Card className={summen.offen > 0 ? 'border-amber-400' : ''}><CardContent className="p-3"><div className="text-xs text-muted-foreground">Offen</div><div className={`text-xl font-extrabold tabular-nums ${summen.offen > 0 ? 'text-amber-700 dark:text-amber-300' : ''}`}>{euro(summen.offen ?? 0)}</div></CardContent></Card>
          {jahre.map(([jahr, betrag]) => (
            <Card key={jahr}><CardContent className="p-3"><div className="text-xs text-muted-foreground">{jahr}</div><div className="text-xl font-extrabold tabular-nums">{euro(betrag)}</div></CardContent></Card>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={lade} disabled={laden} data-testid="rechnungen-neu-laden"><RefreshCw className={`w-4 h-4 ${laden ? 'animate-spin' : ''}`} /></Button>
      </div>

      {d && !d.lexware_kontakt && (
        <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border px-3 py-2">
          Zu dieser Person ist kein Lexware-Kontakt verknüpft — Rechnungen aus Lexware (Altbestand, Shop) erscheinen erst nach „Kontakte abgleichen" im Rechnungslauf. Rechnungen aus dem CRM-Lauf stehen trotzdem hier.
        </p>
      )}

      {/* 31.10.2026: Einzug-Stände nur nach Passwort-Freigabe (wie Umsätze); ohne sie „bezahlt“ statt „eingezogen“, „offen“ statt „Einzug geplant“ */}
      {d?.einzug_gesperrt && inhaber && (
        <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border px-3 py-2" data-testid="rechnungen-einzug-verborgen">
          Einzug-Stände sind verborgen. <button type="button" onClick={anfordern} className="font-semibold text-marke hover:underline">Mit Passwort anzeigen</button>
        </p>
      )}

      {liste.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Noch keine Rechnungen.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Nummer</th>
                <th className="px-3 py-2 text-left font-medium">Datum</th>
                <th className="px-3 py-2 text-left font-medium">Zeitraum / Positionen</th>
                <th className="px-3 py-2 text-right font-medium">Brutto</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Mail</th>
                <th className="px-3 py-2 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {liste.map((r) => (
                <tr key={r.id} className={`border-t border-border align-top ${r.fremder_zahler ? 'opacity-70' : ''}`} data-testid={`rechnung-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap font-medium">
                    {r.nummer || '—'}
                    <div className="text-[10px] text-muted-foreground">{r.quelle === 'crm' ? 'Rechnungslauf' : 'Lexware'}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{datum(r.datum)}</td>
                  <td className="px-3 py-2 min-w-[220px]">
                    <div className="font-medium">{r.titel}</div>
                    {(r.positionen || []).slice(0, 4).map((p, i) => (
                      <div key={i} className="text-xs text-muted-foreground truncate max-w-md">{p.text} · {euro(p.betrag)}</div>
                    ))}
                    {(r.positionen || []).length > 4 && <div className="text-xs text-muted-foreground">… {r.positionen.length - 4} weitere</div>}
                    {r.fuer?.length > 0 && <div className="text-xs text-muted-foreground">für {r.fuer.join(', ')}</div>}
                    {r.fremder_zahler && <div className="text-xs text-amber-700 dark:text-amber-300">Zahler: {r.zahler_name || 'andere Person'}</div>}
                    {r.angefordert && <div className="text-xs text-muted-foreground">aus der App angefordert am {datum(r.angefordert.am)} → {r.angefordert.an}</div>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">{euro(r.art === 'creditnote' ? -r.betrag : r.betrag)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Chip k={r.status} />
                    {r.status_am && <div className="text-[10px] text-muted-foreground mt-0.5">{datum(r.status_am)}</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                    {r.versand ? <>{VERSAND[r.versand.status] || r.versand.status}<div className="text-[10px]">{datum(r.versand.am)}</div></> : 'nie'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!r.pdf_verfuegbar || busy === `pdf-${r.id}`} onClick={() => pdf(r)} data-testid={`rechnung-pdf-${r.id}`}>
                        <FileText className="w-3 h-3 mr-1" />PDF
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!r.pdf_verfuegbar || r.status === 'storniert' || busy === `mail-${r.id}`} onClick={() => senden(r)} data-testid={`rechnung-mail-${r.id}`}>
                        <Mail className="w-3 h-3 mr-1" />Per Mail senden
                      </Button>
                      {r.lexware_link && (
                        <a href={r.lexware_link} target="_blank" rel="noreferrer" className="inline-flex items-center h-7 px-1 text-muted-foreground hover:text-foreground" title="In Lexware öffnen"><ExternalLink className="w-3 h-3" /></a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
